/**
 * TagsInput — saisie libre de tags séparés par virgule ou Enter.
 *
 * Pas de catalogue : c'est volontairement freeform pour permettre aux
 * courtiers d'organiser leurs projets sans qu'on impose une taxonomie.
 *
 * Limites :
 *  - max 20 tags (aligné sur intakeDraftStorage)
 *  - chaque tag max 50 chars
 *  - dédoublonnage case-insensitive (l'affichage garde la casse de la 1ʳᵉ entrée)
 *
 * A11y :
 *  - input texte natif
 *  - chaque chip a un bouton remove avec aria-label
 */

import { useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

const MAX_TAGS = 20;
const MAX_TAG_LENGTH = 50;

type Props = {
  value: readonly string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  id?: string;
};

export function TagsInput({ value, onChange, placeholder, id }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");

  const commit = () => {
    const t = draft.trim();
    if (!t) return;
    if (t.length > MAX_TAG_LENGTH) return;
    if (value.length >= MAX_TAGS) return;
    const lower = t.toLowerCase();
    if (value.some((v) => v.toLowerCase() === lower)) {
      setDraft("");
      return;
    }
    onChange([...value, t]);
    setDraft("");
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
      // Backspace sur input vide → retire le dernier tag (UX courante).
      onChange(value.slice(0, -1));
    }
  };

  const onRemove = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <input
        id={id}
        type="text"
        className="input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={placeholder ?? t("common.tags_placeholder")}
        maxLength={MAX_TAG_LENGTH}
        aria-describedby={id ? `${id}-hint` : undefined}
      />
      {id && (
        <span id={`${id}-hint`} className="mt-1 block text-xs text-slate-400">
          {t("common.tags_hint")}
        </span>
      )}

      {value.length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {value.map((tag, idx) => (
            <li
              key={`${tag}-${idx}`}
              className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
            >
              <span>{tag}</span>
              <button
                type="button"
                onClick={() => onRemove(idx)}
                aria-label={t("common.remove", { item: tag })}
                className="text-slate-400 hover:text-slate-700"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
